import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTheme } from "next-themes";
import {
  Boxes,
  FileText,
  IdCard,
  Search,
  User,
  UserCog,
  Users,
  X,
  type LucideIcon,
} from "lucide-react";
import {
  Background,
  Handle,
  Position,
  ReactFlow,
  useEdgesState,
  useNodesState,
  type Edge,
  type Node,
  type ReactFlowInstance,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  PAG_KIND_META,
  pagNetKindColor,
  pagNetRiskColor,
  type PagNodeKind,
  type PagSeverity,
  type PagTreeNode,
} from "../data/pagTypes";
import { NetworkLegend } from "./NetworkGraph/NetworkLegend";
import { SURFACE as NET_SURFACE } from "./NetworkGraph/networkGraphVisuals";
import {
  GraphFullscreenControl,
  GraphZoomControls,
  useGraphFullscreen,
} from "./NetworkGraph/GraphControls";
import { markGlowShadow } from "@/app/components/ChartsComponent/chartUtils";
import {
  ChartEmptyState,
  ChartLoadingState,
} from "@/app/components/ChartsComponent/ChartStates";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";

const NODE = {
  width: 216,
  height: 28,
  radius: 6,
  paddingX: 8,

  gap: 7,
  iconSize: 12,
  fontSize: 11,
  borderWidth: 1,

  borderAlpha: "ff",
  dimBorderAlpha: "2e",
} as const;

const BADGE = {
  fontSize: 9,
  paddingX: 5,
  height: 14,
  radius: 999,
  bgAlpha: "1f",
} as const;

const LAYOUT = {

  colGap: 88,

  rowPitch: 38,

  groupSpacer: 0.45,
} as const;

const COL_PITCH = NODE.width + LAYOUT.colGap;

const EDGE = {
  width: 1.4,
  litOpacity: 1,
  dimOpacity: 0.3,
  color: { light: "#7C8BA1", dark: "rgba(163,178,199,0.78)" },
} as const;

const SURFACE = {
  light: {
    canvas: "#FFFFFF",
    grid: "#E8EDF4",
    nodeBg: "#FFFFFF",
    nodeText: "#0F172A",
    panel: "rgba(255,255,255,0.95)",
    panelBorder: "#E2E8F0",
    muted: "#94A3B8",
    shadow: "0 1px 2px rgba(15,23,42,0.06)",
  },
  dark: {
    canvas: "#0A1120",
    grid: "#1A2438",
    nodeBg: "#111A2C",
    nodeText: "#E2E8F0",
    panel: "rgba(13,19,34,0.92)",
    panelBorder: "rgba(255,255,255,0.12)",
    muted: "#64748B",
    shadow: "0 1px 2px rgba(0,0,0,0.4)",
  },
} as const;

const KIND_ICON: Record<PagNodeKind, LucideIcon> = {
  application: Boxes,
  account: IdCard,
  user: User,
  group: Users,
  role: UserCog,
  policy: FileText,
};

const FOCUS_ZOOM = 0.6;

const ROOT_X_FRACTION = 0.22;

const MEASURE_MS = 80;

const OVERVIEW_HOLD_MS = 700;

const FLY_MS = 900;
const FIT_PADDING = 0.18;

function layoutTree(treeNodes: PagTreeNode[]): Map<string, { x: number; y: number }> {
  const byId = new Map(treeNodes.map((n) => [n.id, n]));
  const childrenOf = new Map<string, PagTreeNode[]>();
  const roots: PagTreeNode[] = [];

  for (const n of treeNodes) {
    if (n.parentId && byId.has(n.parentId) && n.parentId !== n.id) {
      const kids = childrenOf.get(n.parentId);
      if (kids) kids.push(n);
      else childrenOf.set(n.parentId, [n]);
    } else {
      roots.push(n);
    }
  }

  const positions = new Map<string, { x: number; y: number }>();
  const hasKids = (n: PagTreeNode) => (childrenOf.get(n.id)?.length ?? 0) > 0;
  let leafCursor = 0;

  const place = (node: PagTreeNode, depth: number): number => {
    const existing = positions.get(node.id);
    if (existing) return existing.y;

    const kids = childrenOf.get(node.id) ?? [];
    const x = depth * COL_PITCH;

    let y: number;
    if (kids.length === 0) {
      y = leafCursor * LAYOUT.rowPitch;
      leafCursor += 1;
      positions.set(node.id, { x, y });
    } else {

      positions.set(node.id, { x, y: leafCursor * LAYOUT.rowPitch });

      const ys: number[] = [];
      kids.forEach((kid, i) => {
        if (i > 0 && (hasKids(kids[i - 1]) || hasKids(kid))) {
          leafCursor += LAYOUT.groupSpacer;
        }
        ys.push(place(kid, depth + 1));
      });

      y = (ys[0] + ys[ys.length - 1]) / 2;
      positions.set(node.id, { x, y });
    }
    return y;
  };

  roots.forEach((r) => place(r, 0));
  return positions;
}

export function PagFlowNode({ data }: { data: any }) {
  const kind = data.kind as PagNodeKind;

  const dim: boolean = data.dim;
  const isDark: boolean = data.isDark;
  const color = pagNetKindColor(kind, isDark);
  const sev = data.severity as PagSeverity | null;

  const tooltip = [
    `${PAG_KIND_META[kind].label.replace(/s$/, "")}: ${data.label}`,
    sev ? `Severity: ${sev}` : null,
    ...Object.entries(data.meta ?? {}).map(([k, v]) => `${k}: ${v}`),
  ]
    .filter(Boolean)
    .join("\n");

  const surface = isDark ? SURFACE.dark : SURFACE.light;
  const Icon = KIND_ICON[kind];

  const labelColor = sev ? pagNetRiskColor(sev, isDark) : surface.nodeText;

  const handleStyle = {
    width: 1,
    height: 1,
    minWidth: 1,
    minHeight: 1,
    background: "transparent",
    border: "none",
    opacity: 0,
  } as const;

  return (
    <div
      title={tooltip}
      style={{
        width: NODE.width,
        height: NODE.height,
        display: "flex",
        alignItems: "center",
        gap: NODE.gap,
        padding: `0 ${NODE.paddingX}px`,
        borderRadius: NODE.radius,
        background: surface.nodeBg,
        border: `${NODE.borderWidth}px solid ${color}${dim ? NODE.dimBorderAlpha : NODE.borderAlpha}`,

        boxShadow: dim ? "none" : markGlowShadow(color),
        opacity: dim ? 0.2 : 1,
        transition: "opacity 180ms ease, border-color 180ms ease",
        boxSizing: "border-box",
      }}
    >
      {data.hasParent && <Handle type="target" position={Position.Left} style={handleStyle} />}
      {data.hasChildren && <Handle type="source" position={Position.Right} style={handleStyle} />}

      <Icon
        size={NODE.iconSize}
        color={color}
        strokeWidth={2}
        style={{ flexShrink: 0 }}
        aria-hidden
      />

      <span
        style={{
          flex: 1,
          minWidth: 0,
          fontSize: NODE.fontSize,
          fontWeight: 500,
          lineHeight: 1.2,
          letterSpacing: 0.1,
          color: labelColor,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {data.label}
      </span>

      {data.childCount > 0 && (
        <span
          style={{
            flexShrink: 0,
            display: "inline-flex",
            alignItems: "center",
            height: BADGE.height,
            padding: `0 ${BADGE.paddingX}px`,
            fontSize: BADGE.fontSize,
            fontWeight: 600,
            lineHeight: 1,
            borderRadius: BADGE.radius,
            background: `${color}${BADGE.bgAlpha}`,
            color,
          }}
        >
          {data.childCount}
        </span>
      )}
    </div>
  );
}

const pagNodeTypes = { pagNode: PagFlowNode };

export function PolicyInsightsGraph({
  treeNodes,
  loading = false,
}: {
  treeNodes: PagTreeNode[];
  loading?: boolean;
}) {
  const { theme } = useTheme();
  const isDark = theme !== "light";

  const containerRef = useRef<HTMLDivElement>(null);
  const [flow, setFlow] = useState<ReactFlowInstance | null>(null);
  const { isFullscreen, toggle: toggleFullscreen } = useGraphFullscreen(containerRef);
  const [search, setSearch] = useState("");

  const [hiddenKinds, setHiddenKinds] = useState<Set<PagNodeKind>>(new Set());
  const [hiddenRisks, setHiddenRisks] = useState<Set<PagSeverity>>(new Set());

  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);

  const query = search.trim().toLowerCase();

  const visibleTreeNodes = useMemo(() => {
    if (!query) return treeNodes;

    const byId = new Map(treeNodes.map((n) => [n.id, n]));
    const childrenOf = new Map<string, string[]>();
    treeNodes.forEach((n) => {
      if (!n.parentId) return;
      const kids = childrenOf.get(n.parentId);
      if (kids) kids.push(n.id);
      else childrenOf.set(n.parentId, [n.id]);
    });

    const keep = new Set<string>();

    const keepAncestors = (id: string) => {
      let cur = byId.get(id)?.parentId ?? null;
      while (cur && !keep.has(cur)) {
        keep.add(cur);
        cur = byId.get(cur)?.parentId ?? null;
      }
    };
    const keepDescendants = (id: string) => {
      for (const kid of childrenOf.get(id) ?? []) {
        if (keep.has(kid)) continue;
        keep.add(kid);
        keepDescendants(kid);
      }
    };

    treeNodes.forEach((n) => {
      if (!n.label.toLowerCase().includes(query)) return;
      keep.add(n.id);
      keepAncestors(n.id);
      keepDescendants(n.id);
    });

    return treeNodes.filter((n) => keep.has(n.id));
  }, [treeNodes, query]);

  const base = useMemo(() => {
    const positions = layoutTree(visibleTreeNodes);
    const childCount = new Map<string, number>();
    visibleTreeNodes.forEach((n) => {
      if (n.parentId) childCount.set(n.parentId, (childCount.get(n.parentId) ?? 0) + 1);
    });

    const flowNodes: Node[] = visibleTreeNodes.map((n) => ({
      id: n.id,
      type: "pagNode",
      position: positions.get(n.id) ?? { x: 0, y: 0 },
      data: {
        label: n.label,
        kind: n.kind,
        severity: n.severity ?? null,
        meta: n.meta,
        hasParent: !!n.parentId,
        hasChildren: (childCount.get(n.id) ?? 0) > 0,
        childCount: childCount.get(n.id) ?? 0,
        dim: false,
        isDark,
      },
    }));

    const edgeColor = isDark ? EDGE.color.dark : EDGE.color.light;
    const flowEdges: Edge[] = visibleTreeNodes
      .filter((n) => n.parentId && positions.has(n.parentId))
      .map((n) => ({
        id: `e|${n.parentId}|${n.id}`,
        source: n.parentId as string,
        target: n.id,
        type: "default",
        style: {
          stroke: edgeColor,
          strokeWidth: EDGE.width,
          opacity: EDGE.litOpacity,
        },
      }));

    return { flowNodes, flowEdges };
  }, [visibleTreeNodes, isDark]);

  const isLit = useCallback(
    (n: { kind: PagNodeKind; severity: PagSeverity | null; label: string }) => {
      if (hiddenKinds.has(n.kind)) return false;
      if (n.kind === "policy" && hiddenRisks.has(n.severity ?? "Uncategorized")) return false;
      return true;
    },
    [hiddenKinds, hiddenRisks],
  );

  const laidOutForRef = useRef<PagTreeNode[] | null>(null);

  useEffect(() => {
    const litIds = new Set<string>();
    base.flowNodes.forEach((n) => {
      const d = n.data as any;
      if (isLit({ kind: d.kind, severity: d.severity, label: d.label })) litIds.add(n.id);
    });

    const freshData = laidOutForRef.current !== visibleTreeNodes;
    laidOutForRef.current = visibleTreeNodes;

    setNodes((prev) => {

      const placed = freshData
        ? new Map<string, { x: number; y: number }>()
        : new Map(prev.map((n) => [n.id, n.position]));

      return base.flowNodes.map((n) => ({
        ...n,
        position: placed.get(n.id) ?? n.position,
        data: { ...(n.data as any), dim: !litIds.has(n.id), isDark },
      }));
    });
    setEdges(
      base.flowEdges.map((e) => {
        const on = litIds.has(e.source) && litIds.has(e.target);
        return {
          ...e,
          style: { ...e.style, opacity: on ? EDGE.litOpacity : EDGE.dimOpacity },
        };
      }),
    );
  }, [base, isLit, isDark, setNodes, setEdges, visibleTreeNodes]);

  const focusPoint = useMemo(() => {
    const root =
      base.flowNodes.find((n) => !(n.data as any).hasParent) ?? base.flowNodes[0];
    if (!root) return null;
    return {
      x: root.position.x + NODE.width / 2,
      y: root.position.y + NODE.height / 2,
    };
  }, [base.flowNodes]);

  const reduceMotion = useMemo(
    () =>
      typeof window !== "undefined" &&
      !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches,
    [],
  );

  const flightCancelled = useRef(false);

  useEffect(() => {
    if (!flow || !focusPoint) return;
    flightCancelled.current = false;

    const timers: ReturnType<typeof setTimeout>[] = [];

    if (query) {
      timers.push(
        setTimeout(() => flow.fitView({ padding: FIT_PADDING, duration: 260 }), MEASURE_MS),
      );
      return () => timers.forEach(clearTimeout);
    }

    const aimPoint = () => {
      const width = containerRef.current?.getBoundingClientRect().width ?? 0;
      const shift = width ? (0.5 - ROOT_X_FRACTION) * (width / FOCUS_ZOOM) : 0;
      return { x: focusPoint.x + shift, y: focusPoint.y };
    };

    timers.push(
      setTimeout(() => {

        flow.fitView({ padding: FIT_PADDING, duration: 0 });

        if (reduceMotion) {
          const { x, y } = aimPoint();
          flow.setCenter(x, y, { zoom: FOCUS_ZOOM, duration: 0 });
          return;
        }

        timers.push(
          setTimeout(() => {
            if (flightCancelled.current) return;
            const { x, y } = aimPoint();
            flow.setCenter(x, y, { zoom: FOCUS_ZOOM, duration: FLY_MS });
          }, OVERVIEW_HOLD_MS),
        );
      }, MEASURE_MS),
    );

    return () => timers.forEach(clearTimeout);
  }, [flow, focusPoint, reduceMotion, query]);

  const toggleKind = (k: PagNodeKind) =>
    setHiddenKinds((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });

  const toggleRisk = (s: PagSeverity) =>
    setHiddenRisks((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });

  const surface = isDark ? SURFACE.dark : SURFACE.light;
  const panelBg = surface.panel;
  const panelBorder = surface.panelBorder;

  return (
    <div
      ref={containerRef}
      className="relative w-full overflow-hidden rounded-lg"
      style={{
        height: isFullscreen ? "100vh" : "62vh",
        minHeight: 460,
        background: surface.canvas,
        border: `1px solid ${panelBorder}`,
      }}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={pagNodeTypes}
        onInit={setFlow}
        onMoveStart={(event) => {

          if (event) flightCancelled.current = true;
        }}
        nodesDraggable
        nodesConnectable={false}
        minZoom={0.05}
        maxZoom={2.5}
        fitView
        fitViewOptions={{ padding: FIT_PADDING }}
        proOptions={{ hideAttribution: true }}
      >

        <Background color={surface.grid} gap={22} size={1} />
      </ReactFlow>

      <GraphFullscreenControl
        isFullscreen={isFullscreen}
        onToggle={toggleFullscreen}
        isDark={isDark}
      />

      <GraphZoomControls
        isDark={isDark}
        onZoomIn={() => flow?.zoomIn({ duration: 200 })}
        onZoomOut={() => flow?.zoomOut({ duration: 200 })}
        onFit={() => flow?.fitView({ padding: 0.18, duration: 400 })}
      />

      <div className="absolute right-3 top-3 z-30" style={{ width: 190 }}>
        <div className="relative">
          <Search
            className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2"
            style={{ color: isDark ? "#64748B" : "#94A3B8" }}
          />
          <Input
            type="text"
            value={search}
            maxLength={120}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search"
            className="h-auto w-full rounded-lg py-1.5 pl-8 text-xs outline-none"
            style={{
              paddingRight: search ? 26 : 10,
              background: panelBg,
              border: `1px solid ${panelBorder}`,
              color: isDark ? "#E2E8F0" : "#0F172A",
            }}
          />
          {search && (
            <Button variant="ghost"
              onClick={() => setSearch("")}
              title="Clear search"
              aria-label="Clear search"
              className="justify-start h-auto px-0 py-0 font-normal absolute right-2 top-1/2 -translate-y-1/2"
              style={{ color: isDark ? "#64748B" : "#94A3B8" }}
            >
              <X className="size-3" />
            </Button>
          )}
        </div>
      </div>

      <NetworkLegend
        surface={isDark ? NET_SURFACE.dark : NET_SURFACE.light}
        hiddenKinds={hiddenKinds}
        hiddenRisks={hiddenRisks}
        onToggleKind={toggleKind}
        onToggleRisk={toggleRisk}
      />

      {(loading || treeNodes.length === 0 || visibleTreeNodes.length === 0) && (
        <div
          className="absolute inset-0 z-20 flex items-center justify-center px-4"
          style={{ background: surface.canvas }}
        >
          {loading ? (
            <ChartLoadingState height={300} shape="radial" />
          ) : treeNodes.length === 0 ? (
            <ChartEmptyState
              height={300}
              shape="radial"
              message="No applications carry access data yet, so there is nothing to map."
            />
          ) : (
            <ChartEmptyState
              height={300}
              shape="radial"
              message={`Nothing in this graph matches "${search.trim()}".`}
            />
          )}
        </div>
      )}
    </div>
  );
}
